import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { take } from 'rxjs';
import { AvailableLangs, TranslocoService } from '@ngneat/transloco';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { SharedService } from 'app/services/shared.service';
import { SesionService } from 'app/services/sesion.service';

@Component({
    selector       : 'languages',
    templateUrl    : './languages.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Default,
    exportAs       : 'languages'
})
export class LanguagesComponent implements OnInit, OnDestroy
{
    availableLangs: AvailableLangs;
    activeLang: string;
    flagCodes: any;

    // Mapeo de códigos de idioma a IDs
    private idiomaMap = {
        'es': 0,
        'en': 1
    };

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private _translocoService: TranslocoService,
        private _shared: SharedService,
        private _sesion: SesionService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        debugger;
        // Get the available languages from transloco
        this.availableLangs = this._translocoService.getAvailableLangs();

        // Subscribe to language changes
        this._translocoService.langChanges$.subscribe((activeLang) => {

            // Get the active lang
            this.activeLang = activeLang;

            // Update the navigation
            this._updateNavigation(activeLang);
        });

        // Set the country iso codes for languages for flags
        this.flagCodes = {
            'en': 'us',
            'tr': 'tr'
        };
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     *
     * @param lang
     */
    setActiveLang(lang: string): void
    {
        console.log('=== setActiveLang llamado con:', lang);
        
        // Set the active lang
        this._translocoService.setActiveLang(lang);
        
        // Guardar en localStorage
        localStorage.setItem('lang', lang);
        
        console.log('Llamando a updateIdiomaBackend...');
        
        
        // Actualizar el idioma en el backend si el usuario está autenticado
        this.updateIdiomaBackend(lang);
    }

    /**
     * Actualiza el idioma en el backend
     * 
     * @param lang
     * @private
     */
    private updateIdiomaBackend(lang: string): void
    {
        console.log('=== updateIdiomaBackend llamado con:', lang);
        console.log('datosUser:', this._shared.datosUser);
        
        // Verificar que el usuario esté autenticado
        if (this._shared.isNullOrUndefined(this._shared.datosUser)) {
            console.warn('Usuario no autenticado, no se actualiza idioma en backend');
            return;
        }

        // Obtener el idiomaId correspondiente
        const idiomaId = this.idiomaMap[lang];
        
        console.log('idiomaId obtenido del mapa:', idiomaId);
        
        if (idiomaId === undefined || idiomaId === null) {
            console.warn(`Idioma no mapeado: ${lang}`);
            return;
        }

        console.log('=== Preparando request al backend con idiomaId:', idiomaId);

        // Crear el FormData solo con el idiomaId
        const form = new FormData();
        form.append('idiomaId', `${idiomaId}`);
        
        // Incluir otros campos requeridos del perfil
        if (this._shared.datosUser.nombre) {
            form.append('nombre', this._shared.datosUser.nombre);
        }
        if (this._shared.datosUser.apellidoPaterno) {
            form.append('apellidoPaterno', this._shared.datosUser.apellidoPaterno);
        }
        if (this._shared.datosUser.apellidoMaterno) {
            form.append('apellidoMaterno', this._shared.datosUser.apellidoMaterno);
        }
        if (this._shared.datosUser.telefono) {
            form.append('telefono', this._shared.datosUser.telefono);
        }
        if (this._shared.datosUser.ubicacionId) {
            form.append('ubicacionId', `${this._shared.datosUser.ubicacionId}`);
        }
        form.append('borrarImagen', 'false');

        console.log('=== Enviando request al backend...');

        // Hacer la llamada al backend
        this._sesion.saveWeb(form).subscribe({
            next: (data) => {
                console.log('=== Respuesta del backend:', data);
                if (data.isSuccess) {
                    // Actualizar el idiomaId en los datos del usuario
                    this._shared.datosUser.idiomaId = idiomaId;
                    console.log('✅ Idioma actualizado en el backend correctamente');
                } else {
                    console.error('❌ Error al actualizar idioma:', data.msg);
                }
            },
            error: (error) => {
                console.error('❌ Error al actualizar idioma en el backend:', error);
            }
        });
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the navigation
     *
     * @param lang
     * @private
     */
    private _updateNavigation(lang: string): void
    {
        // For the demonstration purposes, we will only update the Dashboard names
        // from the navigation but you can do a full swap and change the entire
        // navigation data.
        //
        // You can import the data from a file or request it from your backend,
        // it's up to you.

        // Get the component -> navigation data -> item
        const navComponent = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');

        // Return if the navigation component does not exist
        if ( !navComponent )
        {
            return null;
        }

        // Get the flat navigation data
        const navigation = navComponent.navigation;

        // Get the Project dashboard item and update its title
        const projectDashboardItem = this._fuseNavigationService.getItem('dashboards.project', navigation);
        if ( projectDashboardItem )
        {
            this._translocoService.selectTranslate('Project').pipe(take(1))
                .subscribe((translation) => {

                    // Set the title
                    projectDashboardItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }

        // Get the Analytics dashboard item and update its title
        const analyticsDashboardItem = this._fuseNavigationService.getItem('dashboards.analytics', navigation);
        if ( analyticsDashboardItem )
        {
            this._translocoService.selectTranslate('Analytics').pipe(take(1))
                .subscribe((translation) => {

                    // Set the title
                    analyticsDashboardItem.title = translation;

                    // Refresh the navigation component
                    navComponent.refresh();
                });
        }
    }
}
